import { isAxiosError } from 'axios';
import { EXTERNAL_WAYBILL_RECORDED, externalWaybillLine } from '@/utils/fulfillmentCarrierVoid';
import type { SwitchCarrierOrderRequest, SwitchCarrierProvider } from '@/infrastructure/FulfillmentAdminClient';

/** The carriers the switch offers, in OMS Center's order. */
export const SWITCH_CARRIER_PROVIDERS: ReadonlyArray<{ value: SwitchCarrierProvider; label: string }> = [
  { value: 'DELEO', label: 'DELEO' },
  { value: 'FEDEX', label: 'FEDEX' },
  { value: 'PARXL', label: 'PARXL' },
  { value: 'YUN_EXPRESS', label: 'YUN EXPRESS' },
];

/** A carrier order that has not left yet is the only one the facade lets a switch replace. */
export const SWITCHABLE_FULFILLMENT_STATUSES = ['ORDERED', 'AWAITING_INBOUND', 'READY'];

/** A hand-typed carrier reads `Yun Express` or `yun-express`; the switch compares it as the provider it names. */
export const providerOf = (carrier: string | null | undefined): SwitchCarrierProvider | null => {
  const normalized = (carrier ?? '')
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, '_');
  const compact = normalized.replace(/_/g, '');
  if (compact === 'YUNEXPRESS' || compact === 'YUN') {
    return 'YUN_EXPRESS';
  }
  return SWITCH_CARRIER_PROVIDERS.find((provider) => provider.value === normalized)?.value ?? null;
};

/**
 * One switch the operator sent but has not seen finish. The key is kept with it so a retry resumes the
 * same switch: a fresh key after an uncertain answer could cancel or book a second time.
 */
export interface CarrierSwitchDraft {
  request: SwitchCarrierOrderRequest;
  /** The carrier the dialog showed, so a resumed dialog can still name it after the cancel cleared it. */
  sourceCarrier: string;
  /** A kept draft is always one whose send may be past the cancel, so its key is never replaced. */
  pastCancel: boolean;
}

const storageKey = (orderId: number) => `fulfillment:carrier-switch:v1:${orderId}`;

export const readCarrierSwitchDraft = (orderId: number): CarrierSwitchDraft | null => {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(storageKey(orderId)) ?? 'null');
    const draft = parsed as Partial<CarrierSwitchDraft> | null;
    const request = draft?.request;
    if (
      !request ||
      !SWITCH_CARRIER_PROVIDERS.some((provider) => provider.value === request.targetProvider) ||
      typeof request.expectedCarrierOrderId !== 'string' ||
      !request.expectedCarrierOrderId ||
      typeof request.idempotencyKey !== 'string' ||
      !request.idempotencyKey ||
      typeof request.reason !== 'string' ||
      typeof draft.sourceCarrier !== 'string'
    ) {
      return null;
    }
    return {
      request: {
        targetProvider: request.targetProvider,
        expectedCarrierOrderId: request.expectedCarrierOrderId,
        idempotencyKey: request.idempotencyKey,
        reason: request.reason,
      },
      sourceCarrier: draft.sourceCarrier,
      pastCancel: draft.pastCancel === true,
    };
  } catch {
    return null;
  }
};

/** Saved before the request leaves; storage that refuses throws, so the switch fails closed rather than losing its key. */
export const saveCarrierSwitchDraft = (orderId: number, draft: CarrierSwitchDraft): void => {
  localStorage.setItem(storageKey(orderId), JSON.stringify(draft));
};

export const clearCarrierSwitchDraft = (orderId: number): void => {
  try {
    localStorage.removeItem(storageKey(orderId));
  } catch {
    // Storage that cannot be written held nothing to clear.
  }
};

/**
 * What a refusal means for the key. `renew`: nothing was cancelled or booked, so the dialog starts over
 * with a fresh key. `keep`: resend the same key to retry or resume. `retire`: the key can never switch
 * this order again — it ended without a recorded booking, or the order moved on — so the dialog stops.
 */
export type SwitchKeyFate = 'renew' | 'keep' | 'retire';

export interface SwitchRefusal {
  message: string;
  fate: SwitchKeyFate;
}

interface SwitchErrorBody {
  code?: string;
  message?: string | string[];
  /** Set on EXTERNAL_WAYBILL_RECORDED (SSD-7586): the waybill booked outside OMS that stopped the booking. */
  carrier?: string | null;
  trackingNumber?: string;
}

interface SwitchContext {
  target: string;
  sourceCarrier: string;
  sourceCarrierOrderId: string;
  /** An earlier answer for this key left the source possibly cancelled. */
  pastCancel: boolean;
}

const RESEND = 'Send again to retry — the same key resumes this switch and never cancels twice.';
const UNKNOWN_OUTCOME =
  'The carrier result is unknown. Do not start a new switch: send again to resume this one with the same key, or check the carrier console. Slack was alerted.';

/** The facade's own sentence, or nothing; validation answers with a list. */
const serverWords = (body: SwitchErrorBody | undefined): string | undefined => {
  const text = Array.isArray(body?.message) ? body.message.join(' · ') : body?.message;
  return text || undefined;
};

/** Every refusal the switch route documents, worded as what the operator does next. */
export const describeSwitchFailure = (error: unknown, context: SwitchContext): SwitchRefusal => {
  const response = isAxiosError<SwitchErrorBody>(error) ? error.response : undefined;
  if (!response) {
    // No answer at all: the request may have reached the facade and be cancelling right now.
    return { message: UNKNOWN_OUTCOME, fate: 'keep' };
  }
  const { status, data } = response;
  const said = serverWords(data);
  // A refusal before the cancel frees the key, unless an earlier attempt with it may already be past the cancel.
  const definite = (message: string, onRenew = ''): SwitchRefusal =>
    context.pastCancel
      ? { message: `${message} ${RESEND}`, fate: 'keep' }
      : { message: `${message}${onRenew}`, fate: 'renew' };
  // Refusals after which this key can never switch the order again, whatever it did before.
  const ended = (message: string): SwitchRefusal => ({ message, fate: 'retire' });
  switch (data?.code) {
    case 'WEB_ORDER_BEFORE_LAUNCH':
      return definite(webOrderBeforeLaunch('switch its carrier'));
    // The order holds a waybill booked outside OMS (SSD-7586): no carrier order stands behind it to cancel or switch.
    case EXTERNAL_WAYBILL_RECORDED:
      return definite(
        externalWaybillLine({ carrier: data.carrier ?? null, trackingNumber: data.trackingNumber ?? '' }),
      );
    case 'SAME_SHIPPING_PROVIDER':
      return definite(`This order already ships with ${context.target}. Choose a different carrier.`);
    case 'FEDEX_ADDRESS_INVALID':
    case 'FEDEX_PREFLIGHT_VALIDATION_FAILED':
      return definite(
        `FedEx refused this order, so this attempt cancelled nothing: ${said ?? data.code}. Fix it and try again.`,
      );
    case 'ORDER_NOT_SWITCHABLE':
      return ended(
        'This order has shipped or is no longer ORDERED, AWAITING_INBOUND or READY, so its carrier cannot be switched.',
      );
    case 'SOURCE_SHIPMENT_CHANGED':
      return ended(
        `The carrier order changed since this dialog opened (it showed ${context.sourceCarrierOrderId}). The order was reloaded — open Switch Provider again.`,
      );
    case 'MANUAL_CANCELLATION_REQUIRED':
      return definite(
        `Parxl carrier order ${context.sourceCarrierOrderId} cannot be cancelled from here. Cancel it in the Parxl Hub, clear the tracking number, then book ${context.target} with Reset Order.`,
      );
    case 'IDEMPOTENCY_KEY_REUSED':
      return ended(
        'This switch was already sent with different inputs. Open Switch Provider again to start a new one.',
      );
    case 'CARRIER_NOT_CANCELLABLE':
      return definite(
        `The carrier "${context.sourceCarrier}" was typed by hand and cannot be cancelled from here. Clear the tracking number first.`,
      );
    case 'SHIPMENT_SWITCH_IN_PROGRESS':
      return {
        message:
          'This switch is still running in another request. Wait a minute, then send again — the same key picks it up.',
        fate: 'keep',
      };
    case 'SHIPMENT_RECONCILIATION_REQUIRED':
    case 'SHIPMENT_PROVIDER_FAILED':
      return {
        message: `An earlier attempt may have booked ${context.target} without recording it. Do not switch again: find the parcel in the ${context.target} console and record its waybill with Change Tracking #.`,
        fate: 'retire',
      };
    case 'SHIPMENT_PROVIDER_VALIDATION_FAILED':
      return {
        message: `${context.sourceCarrier} ${context.sourceCarrierOrderId} is cancelled, but ${context.target} refused the new booking: ${said ?? 'no reason given'}. Fix it and send again — the same key books ${context.target} without cancelling again.`,
        fate: 'keep',
      };
    case 'SHIPMENT_CANCELLATION_REJECTED':
    case 'SHIPMENT_CANCELLATION_NOT_CONFIRMED':
      return definite(
        `${context.sourceCarrier} did not cancel ${context.sourceCarrierOrderId}, so nothing was booked: ${said ?? data.code}.`,
        ' Try again, or pick another carrier.',
      );
    case 'SHIPMENT_CANCELLATION_UNKNOWN':
    case 'SHIPMENT_CANCELLATION_RECONCILIATION_REQUIRED':
      return { message: UNKNOWN_OUTCOME, fate: 'keep' };
    default:
      break;
  }
  if (status === 404) {
    return ended('This order is not in Fulfillment 2.0.');
  }
  if (status >= 500) {
    return { message: UNKNOWN_OUTCOME, fate: 'keep' };
  }
  // A guard this screen does not know by name (a closing session, say) refuses before the cancel.
  return definite(`${said ?? 'The switch was refused.'} This attempt cancelled nothing.`);
};

/**
 * The refusal every 2.0 carrier action answers on a web order while the launch switch is off (SSD-7060):
 * OMS Center still owns that carrier order, so the sentence sends the operator there.
 */
export const webOrderBeforeLaunch = (action: string): string =>
  `Web order before launch: until Fulfillment 2.0 launches, OMS Center owns this order's carrier order. Use OMS Center to ${action}.`;
